import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  IFRAME_EXTERNAL_ID_PROVIDER_CHECK_VALUE,
  IFRAME_EXTERNAL_ID_PROVIDER_INIT_MESSAGE,
} from 'constants/auth.constants';
import { environment } from '../../../environments/environment';
import { DEFAULT_PRODUCTION_DOMAIN_NAME } from '../../global';
import { ExternalLoginSettings, ExternalLoginRequestPayload } from 'interfaces/external-login.interface';
import { EntryService } from 'services/entry/entry.service';
import { SIGN_IN_TYPES } from 'pages/entry/entry.helpers';

const ORIGIN = '*';

@Component({
  selector: 'cm-iframe-external-login',
  templateUrl: './iframe-external-login.component.html',
  styleUrls: ['./iframe-external-login.component.scss'],
})
export class IframeExternalLoginComponent implements OnInit, AfterViewInit {
  showExternalLogin: boolean = environment.showExternalLogin;
  @Input() verifyUserByToken: boolean = false;
  @Input() settings: ExternalLoginSettings = null;
  @ViewChild('iframe', { static: true }) iframe?: ElementRef;
  @Output() callback = new EventEmitter();

  constructor(private activatedRoute: ActivatedRoute, private entryService: EntryService) {}

  private isProductionEnvironment(): boolean {
    return environment.name.toLowerCase() === 'production'; // move DomainService.ENVIRONMENT_NAME to constant and use in both places
  }

  private isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === 'local.certemy.com';
  }

  private getIframeUrl() {
    if (!this.showExternalLogin) {
      return '';
    }

    const url = 'external-login';

    if (this.isLocalhost()) {
      return `${window.location.protocol}//${window.location.host}/${url}`;
    }

    if (this.isProductionEnvironment()) {
      return `https://${DEFAULT_PRODUCTION_DOMAIN_NAME}.certemy.com/${url}`;
    }

    const environmentName = environment.name.toLowerCase();
    return `https://${environmentName}.certemy.com/${url}`;
  }

  ngOnInit() {
    this.activatedRoute.data.subscribe(() => {
      // disable external logins only for specific subdomains
      if (window.location.href.indexOf('ami') !== -1) {
        this.showExternalLogin = false;
      }

      const iframe = this.iframe.nativeElement;
      const url = this.getIframeUrl();
      iframe.src = url;
      if (this.showExternalLogin && url) {
        iframe.className = 'show';

        iframe.onload = () => {
          const contentWindow = iframe.contentWindow;
          // TODO - buttons for external login are not displayed
          setTimeout(() => {
            contentWindow.postMessage(
                {
                  [IFRAME_EXTERNAL_ID_PROVIDER_INIT_MESSAGE]: true,
                  verifyUserByToken: this.verifyUserByToken,
                  settings: this.settings,
                },
                ORIGIN,
            );
          }, 1000);
        };
      }
    });
  }

  ngAfterViewInit() {
    window.addEventListener('message', event => {
      if (event.data) {
        // if data has [IFRAME_EXTERNAL_ID_PROVIDER_KEY] value and its value is true
        const { [IFRAME_EXTERNAL_ID_PROVIDER_CHECK_VALUE]: checkValue, type, height, ...data } = event.data;
        if (checkValue) {
          if (height) {
            this.iframe.nativeElement.style.height = `${height + 20}px`;
            return;
          }
          const payload: ExternalLoginRequestPayload = {
            organizationId: this.settings.organizationId,
            redirectUrl: this.settings.url,
          };
          if (this.settings.data) {
            payload.data = this.settings.data;
          }
          switch (type) {
            case SIGN_IN_TYPES.ONE_LOGIN:
              return this.entryService.oneLoginBtnClick(payload);
            case SIGN_IN_TYPES.OKTA:
              return this.entryService.oktaBtnClick(payload);
            case SIGN_IN_TYPES.AZURE:
              return this.entryService.azureBtnClick(payload);
            case SIGN_IN_TYPES.ENTRA:
              return this.entryService.entraBtnClick(payload);
            case SIGN_IN_TYPES.PING:
              return this.entryService.pingBtnClick(payload);
          }
          this.callback.emit({
            type,
            ...data,
          });
        }
      }
    });
  }
}
